import { useState, type ReactElement } from 'react';
import { ApiError } from '@molt/shared';
import { Button, TextField } from '@/components';
import { useCreateComment } from '@/lib/comments';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * The inline reply composer — `FR-NOTIF-009`: *"Comment and mention
 * notifications additionally expose an inline reply box in the drawer that
 * posts a comment to the source card without navigating away."*
 *
 * ⚠⚠ **THE `boardReadable` PROP IS GONE, AND THE GATE MOVED RATHER THAN
 * DISAPPEARED.** It stood here through fan-out 7 for one reason its own
 * docstring recorded: `canReplyInline` in `apps/web/src/lib/notifications.ts`
 * already refused a deleted board, a closed board and a deleted card, and
 * readability belonged beside them, but that module was outside that chunk's
 * file list. It is inside this one. `inlineReplyState(row)` now answers
 * `'open' | 'noAccess' | 'none'` in one place, `NotificationRow` renders
 * `notifications.replyNoAccess` for the middle value, and **this component is
 * only ever mounted for `'open'`** — so it has no access branch left to take
 * and no dead branch with a green test over it.
 *
 * `FR-NOTIF-009` AC3's *"a 'you no longer have access' message"* is still
 * rendered, by the row; the fact that the composer's absence and the message
 * are now decided together is the point of moving it.
 */
export interface NotificationReplyProps {
  readonly boardId: string;
  readonly cardId: string;
}

export function NotificationReply({
  boardId,
  cardId,
}: NotificationReplyProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createComment = useCreateComment();

  function submit() {
    const trimmed = body.trim();
    if (trimmed === '') {
      setError(t('notifications.replyEmpty'));
      return;
    }
    setError(null);
    createComment.mutate(
      { boardId, cardId, body: trimmed },
      {
        onSuccess: () => setBody(''),
        onError: (cause) => {
          setError(
            cause instanceof ApiError && cause.message !== ''
              ? cause.message
              : t('notifications.replyFailed'),
          );
        },
      },
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-2" data-reply-access="open">
      <TextField
        label={t('notifications.replyLabel')}
        multiline
        value={body}
        onChange={(event) => {
          setBody(event.currentTarget.value);
          if (error !== null) setError(null);
        }}
        {...(error === null ? {} : { error })}
      />
      <Button size="sm" variant="primary" busy={createComment.isPending} onClick={submit}>
        {t('notifications.replyButton')}
      </Button>
    </div>
  );
}
